import { css } from "@emotion/react"
import { useCallback, useMemo } from "react"
import { colors, PitchBg } from "shared-ui"
import sensorIcon from '@assets/icons/sensor.svg'
import { ReactSVG } from "react-svg"
import { darken } from "polished"
import BatteryIndicator from "./BatteryIndicator"
import { useIntl } from "react-intl"

// according to specs this is going to be a static 3x3 grid, forever and ever

// we'll keep the same X and Y axis orientation as for the analyses, to keep the weirdness consistent:
// |x3y1        x3y3
// |
// |x1y1        x1y3
// -------------------

export default function SensorLocationPicker({
    posX,
    posY,
    handleSetCoordinates,
    handleClick,
    isUnderCover = false,
    disabled,
    otherDevices = [],
    pickAnyPosition = false,
    showGrid = true,
    showPitchBg = false,
    pitchBg,
    showBattery = false,
    showPlacementLabel = false,
    underLay,
    ...props }) {

    const readOnly = useMemo(() => {
        return (!handleSetCoordinates && !handleClick) || disabled
    }, [handleSetCoordinates, handleClick, disabled])

    const handleCellClick = useCallback((x, y) => {
        if (readOnly) return
        if (handleSetCoordinates) {
            if (posX === x && posY === y) {
                handleSetCoordinates(null, null)
            } else {
                handleSetCoordinates(x, y)
            }
        } else if (handleClick) {
            handleClick(x, y)
        }
    }, [handleSetCoordinates, readOnly, posX, posY, handleClick])

    return <div css={css`
            width: 100%;
            aspect-ratio: 4.5 / 3;

            display: grid;
            grid-template: 1fr 1fr 1fr / 1fr 1fr 1fr;
            gap: ${showGrid ? 2 : 0}px;
            border: 2px solid transparent;
            background: ${showGrid ? colors.soft : 'transparent'};
            border-radius: 3px;
            position: relative;
        `}
        {...props}
    >
        {underLay &&
            <div css={css`
                position: absolute;
                width: 100%;
                height: 100%;
                > * {
                    width: 100%;
                    height: 100%;
                }
            `}>
                {underLay}
            </div>
        }

        {[
            [3, 1], [3, 2], [3, 3],
            [2, 1], [2, 2], [2, 3],
            [1, 1], [1, 2], [1, 3]
        ].map(([x, y]) => {

            const isSelected = x === posX && y === posY
            // if we're not editing a specific sensor, but viewing all sensors of the pitch, all sensors are 'otherDevices'
            const occupiedDevice = otherDevices.find(device => device.latestDevicePlacementEvent?.posX === x && device.latestDevicePlacementEvent?.posY === y)
            const occupiedMode = occupiedDevice?.latestDevicePlacementEvent.deviceMode
            const isOccupied = !!occupiedDevice && (occupiedMode !== 'unassigned')

            const mode = (isSelected && isOccupied && !pickAnyPosition) ? 'error'
                : (isSelected && isOccupied && pickAnyPosition) ? 'selected-occupied'
                    : (isSelected && !pickAnyPosition) ? 'selected'
                        : isSelected ? 'selected-empty'
                            : (isOccupied) ? 'occupied'
                                : 'empty'

            return <Cell
                key={`${x}${y}SensorPickerCell`}
                setCoordinates={() => { if (!isOccupied || pickAnyPosition) { handleCellClick(x, y) } }}
                disabled={(readOnly || isOccupied) && !pickAnyPosition}
                mode={mode}
                isUnderCover={!isOccupied ? isUnderCover : (occupiedMode === 'under-cover')}
                showGrid={showGrid}
                battery={occupiedDevice?.latestDeviceStatusEvent?.batteryLevel}
                showBattery={showBattery}
                showPlacementLabel={showPlacementLabel}
                deviceName={occupiedDevice?.name}
            />
        })}

        <div css={css`
            position: absolute;
            width: 100%;
            height: 100%;
            z-index: 0;
        `}>
            {showPitchBg &&
                <PitchBg type={pitchBg} />
            }
        </div>
    </div>
}
/* TO DO: TEST SENSOR PICKER IN FORM AGAIN */
const selectedColor = darken(0.15)(colors.main1)

function Cell({ setCoordinates, mode, disabled, isUnderCover, showGrid, battery, showBattery, showPlacementLabel, deviceName, ...props }) {

    const empty = mode === 'empty' || mode === 'selected-empty'
    const selected = mode === 'selected' || mode === 'selected-occupied' || mode === 'selected-empty'

    const style = useMemo(() => {

        return {
            cell: css` 
                background: ${selected ? selectedColor : (showGrid ? colors.eventLight : 'transparent')};
                height: 100%;
                width: 100%;
                overflow: hidden;
                z-index: 1;
                position: relative;
            ${!disabled ?
                    `cursor: pointer;
                    &:hover {
                        opacity: ${mode === 'selected' ? 0.75 : 0.4};
                        background: ${selectedColor};
                    }`
                    :
                    ''}`
            ,
            //icon
            icon: css`
                width: 100%;
                height: 100%;
                color: ${mode === 'error' ? 'red' :
                    (isUnderCover ? 'black' : 'white')
                };
                display: grid;
                place-items: center;

            
            > * {
                width: 50%;
                display: grid; 
                place-items: center;
                    }
                    
            `,
            //text
            text: css`
                position: absolute;
                bottom: 0;
                left: 50%;
                transform: translate(-50%, -2px);
                color: ${(isUnderCover ? 'black' : 'white')};
            `,
            batteryWrapper: css`
                width: 100%;
                height: 100%;
                display: grid;
                place-items: center;
                font-size: 1.3em;
            `,

        }

    }, [selected, showGrid, disabled, mode, isUnderCover])

    const { formatMessage } = useIntl()

    return <div
        title={deviceName}
        onClick={setCoordinates}
        css={style.cell} >

        {showBattery && (!empty)
            ?
            <div css={style.batteryWrapper}>
                <BatteryIndicator
                    percentage={battery}
                    backgroundColor={colors.eventDark}
                />
            </div>
            :
            (mode !== 'empty' && mode !== 'selected-empty') && <>
                <ReactSVG
                    src={sensorIcon}
                    css={style.icon}
                />
                {showPlacementLabel &&
                    <div css={style.text}>
                        {isUnderCover ? formatMessage({ id: 'under' }) : formatMessage({ id: 'over' })}
                    </div>
                }
            </>
        }

    </ div>
}